import { Link } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";

const heroImages = [
  {
    src: "https://readdy.ai/api/search-image?query=stunning%20commercial%20aircraft%20Boeing%20787%20Dreamliner%20flying%20above%20dramatic%20clouds%20at%20sunset%20golden%20hour%2C%20cinematic%20aviation%20photography%2C%20deep%20blue%20sky%20with%20orange%20horizon%2C%20professional%20aerial%20photography%2C%20wide%20angle%20panoramic%20view%2C%20majestic%20flight&width=1920&height=1080&seq=hero-main-aviation&orientation=landscape",
    alt: "Aviación Insight RD - Boeing 787 al atardecer",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Caribbean%20island%20aerial%20view%20aircraft%20approach%20turquoise%20water%20tropical%20paradise%20Dominican%20Republic%20coastline%2C%20wide%20body%20jet%20landing%20approach%20beautiful%20scenery%2C%20cinematic%20aerial%20photography%20sunset&width=1920&height=1080&seq=hero-caribe-2&orientation=landscape",
    alt: "Aviación sobre el Caribe dominicano",
  },
  {
    src: "https://readdy.ai/api/search-image?query=modern%20international%20airport%20terminal%20at%20night%20with%20runway%20lights%20aircraft%20departing%2C%20long%20exposure%20dramatic%20photography%2C%20vibrant%20orange%20blue%20colors%2C%20professional%20aviation%20nightscape%2C%20cinematic%20wide%20angle&width=1920&height=1080&seq=hero-night-3&orientation=landscape",
    alt: "Aeropuerto nocturno con pista iluminada",
  },
  {
    src: "https://readdy.ai/api/search-image?query=commercial%20jet%20aircraft%20dramatic%20takeoff%20climbing%20steeply%20blue%20sky%20white%20clouds%20below%2C%20powerful%20engines%20trail%2C%20professional%20aviation%20photography%2C%20wide%20angle%20cinematic%20view%2C%20epic%20departure%20shot&width=1920&height=1080&seq=hero-takeoff-4&orientation=landscape",
    alt: "Despegue espectacular de avión comercial",
  },
];

export default function HeroSection() {
  const [currentImg, setCurrentImg] = useState(0);
  const [prevImg, setPrevImg] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [loadedImgs, setLoadedImgs] = useState<Set<number>>(new Set([0]));
  const imgRefs = useRef<(HTMLImageElement | null)[]>([]);

  const scrollToContent = useCallback(() => {
    window.scrollTo({ top: window.innerHeight - 60, behavior: "smooth" });
  }, []);

  const scrollToNewsletter = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.getElementById("newsletter");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    const nextIdx = (currentImg + 1) % heroImages.length;
    const img = new Image();
    img.src = heroImages[nextIdx].src;
    img.onload = () => setLoadedImgs((prev) => new Set([...prev, nextIdx]));
  }, [currentImg]);

  const goToNext = useCallback((idx: number) => {
    if (transitioning) return;
    setPrevImg(currentImg);
    setTransitioning(true);
    setTimeout(() => {
      setCurrentImg(idx);
      setTransitioning(false);
      setPrevImg(null);
    }, 1100);
  }, [currentImg, transitioning]);

  useEffect(() => {
    const interval = setInterval(() => {
      const next = (currentImg + 1) % heroImages.length;
      goToNext(next);
    }, 6000);
    return () => clearInterval(interval);
  }, [currentImg, goToNext]);

  const handleImgLoad = (idx: number) => {
    setLoadedImgs((prev) => new Set([...prev, idx]));
  };

  return (
    <section className="relative w-full h-[360px] sm:h-[400px] md:h-[420px] lg:h-[460px] flex items-center justify-center overflow-hidden">

      {heroImages.map((img, i) => {
        const isActive = i === currentImg && !transitioning;
        const isExiting = i === prevImg && transitioning;
        const isEntering = i === currentImg && transitioning;

        return (
          <div
            key={img.src}
            className="absolute inset-0"
            style={{
              opacity: isActive ? 1 : isExiting ? 0 : isEntering ? 0 : 0,
              transition: isActive || isEntering
                ? 'opacity 1.1s cubic-bezier(0.22, 1, 0.36, 1)'
                : isExiting
                ? 'opacity 1.1s cubic-bezier(0.22, 1, 0.36, 1)'
                : 'none',
              zIndex: isActive ? 2 : isExiting ? 1 : 0,
            }}
          >
            {!loadedImgs.has(i) && (
              <div className="absolute inset-0 bg-[#0B1F3A] animate-pulse" />
            )}
            <img
              ref={(el) => { imgRefs.current[i] = el; }}
              src={img.src}
              alt={img.alt}
              onLoad={() => handleImgLoad(i)}
              loading={i === 0 ? "eager" : "lazy"}
              className="w-full h-full object-cover object-center"
              style={{
                transform: isActive ? 'scale(1.04)' : 'scale(1.0)',
                transition: 'transform 6s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            />
          </div>
        );
      })}

      <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A]/80 via-[#0B1F3A]/50 to-[#0B1F3A]/88 z-10" />

      {/* Carousel dots */}
      <div className="absolute bottom-16 sm:bottom-16 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {heroImages.map((_, i) => (
          <button
            key={i}
            onClick={() => goToNext(i)}
            className={`rounded-full transition-all duration-400 cursor-pointer ${
              i === currentImg ? "w-7 h-2.5 bg-white" : "w-2.5 h-2.5 bg-white/35 hover:bg-white/60"
            }`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-20 w-full text-center px-4 md:px-6 max-w-5xl mx-auto">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/8 border border-white/20 rounded-full px-5 py-2 mb-4 backdrop-blur-md">
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          <span
            className="text-white/80 text-xs font-semibold tracking-widest uppercase"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Noticias del sector aeronáutico de RD y el Caribe
          </span>
        </div>

        {/* Title */}
        <h1
          className="text-white font-extrabold leading-none mb-3 tracking-tight whitespace-nowrap"
          style={{ fontFamily: "'Sora', sans-serif", fontSize: "clamp(2rem, 5vw, 4rem)" }}
        >
          AVIACIÓN INSIGHT RD
        </h1>

        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mt-4">
          <Link
            to="/actualidad"
            className="w-full sm:w-auto px-5 py-2.5 sm:px-8 sm:py-3.5 md:px-12 md:py-4 bg-white text-[#0B1F3A] font-extrabold text-sm sm:text-base rounded-xl transition-all tracking-wide whitespace-nowrap cursor-pointer hover:bg-blue-50 hover:scale-105 active:scale-95 shadow-lg shadow-black/20"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '0.04em' }}
          >
            <i className="ri-newspaper-line mr-2 text-base" />
            Ver Noticias
          </Link>
          <a
            href="#newsletter"
            onClick={scrollToNewsletter}
            className="w-full sm:w-auto px-5 py-2.5 sm:px-8 sm:py-3.5 md:px-10 md:py-4 bg-transparent hover:bg-white/12 border-2 border-white/40 text-white font-semibold text-sm rounded-xl transition-all tracking-wide whitespace-nowrap cursor-pointer backdrop-blur-sm hover:scale-105 active:scale-95"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            <i className="ri-mail-send-line mr-2" />
            Suscribirse
          </a>
        </div>
      </div>

      {/* Scroll down */}
      <button
        onClick={scrollToContent}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 cursor-pointer group z-20 bg-transparent border-none outline-none"
        aria-label="Bajar al contenido"
      >
        <span
          className="text-white/40 text-[10px] tracking-widest uppercase group-hover:text-white/70 transition-colors"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          Explorar
        </span>
        <div className="w-7 h-7 flex items-center justify-center border border-white/25 rounded-full group-hover:border-white/55 group-hover:bg-white/10 transition-all animate-bounce">
          <i className="ri-arrow-down-line text-white/50 group-hover:text-white text-sm" />
        </div>
      </button>
    </section>
  );
}